// « Proposition » : cartes des variantes classées (et la proposition du technicien évaluée sur les mêmes critères), plan annoté
// par calques, légende permanente, points bloquants et « à vérifier » cliquables (centrage sur le plan), fiche de l'objet touché,
// et décisions (retenir, rejeter, reprendre comme base de travail, valider) selon les droits.
import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { AlertTriangle, Layers } from 'lucide-react';
import { useSession } from '../../../lib/session.tsx';
import { Badge, Chargement, EtatErreur, Panneau } from '../../../components/ui.tsx';
import type { VarianteProposition } from '@shared/proposition/types.ts';
import { LIBELLES_STRATEGIE } from '@shared/proposition/types.ts';
import type { PlanVue } from '../modele.ts';
import { PlanCalque } from './PlanCalque.tsx';
import { CALQUES, CalquesVariante, DefsCalques, resumePiece, TEINTES, type Calque, type Selection } from './CalquesVariante.tsx';
import { Decision } from './Decision.tsx';
import { MaterielVariante } from './Materiel.tsx';
import { useDetail } from './donnees.ts';
import {
  emprise, kw, LIBELLE_STATUT, LIBELLE_TON, metres, montantHt, nombre, pct, pointsLocalises, stylesDiametres, type CalculVue, type LigneVue, type PieceVue, type PointLocalise, type TonPiece,
} from './logique.ts';

export const TOUS_CALQUES = new Set<Calque>(CALQUES.map((c) => c[0]));

/** Carte d'une variante : rang, origine, statut et chiffres clés (jamais de montant sans le droit aux coûts). */
export function CarteVariante({ l, choisie, choisir, couts }: { l: LigneVue; choisie: boolean; choisir: () => void; couts: boolean }) {
  const c = l.criteres;
  return (
    <button type="button" className={`prp-carte ${choisie ? 'est-choisi' : ''} ${l.origine === 'technicien' ? 'prp-carte-tech' : ''}`} aria-pressed={choisie} onClick={choisir}>
      <span className="prp-carte-tete">
        <span className="prp-rang" aria-label={`Rang ${l.rang}`}>{l.rang}</span>
        <span className="prp-carte-titre">{l.libelle}</span>
      </span>
      <span className="prp-carte-badges">
        <Badge ton={l.origine === 'technicien' ? 'info' : 'accent'} icone={false}>{l.origine === 'technicien' ? 'Technicien' : 'TRAME'}</Badge>
        {l.statut !== 'calculee' && <Badge ton={l.statut === 'rejetee' || l.statut === 'perimee' ? 'ambre' : 'succes'} icone={false}>{LIBELLE_STATUT[l.statut]}</Badge>}
      </span>
      <span className="prp-carte-chiffres tres-petit">
        <span className={c.bloquants ? 'prp-txt-danger' : ''}>{c.bloquants} bloquant{c.bloquants > 1 ? 's' : ''}</span>
        <span>{c.aVerifier} à vérifier</span>
        <span>{c.unites.ui} UI + {c.unites.ue} UE</span>
        <span>{metres(c.cuivre.equivalentM)} éq.</span>
        {couts && !c.cout.masque && <span>{c.cout.complet && c.cout.totalHt ? montantHt(c.cout.totalHt) : 'coût incomplet'}</span>}
      </span>
    </button>
  );
}

export function Legende({ v, styles }: { v: VarianteProposition; styles: ReturnType<typeof stylesDiametres> }) {
  const types = [...new Set(v.unites.map((u) => u.type))];
  const tons: TonPiece[] = ['couvert', 'non_couvert', 'incomplet', 'non_traitee'];
  return (
    <div className="prp-legende" aria-label="Légende du plan">
      <div className="prp-legende-groupe"><span className="prp-legende-titre">Pièces</span>
        {tons.map((t) => <span key={t} className="prp-legende-item"><svg width="22" height="14" aria-hidden><DefsCalques /><rect x="1" y="1" width="20" height="12" fill={TEINTES[t].fond} stroke={TEINTES[t].trait} strokeWidth="2" strokeDasharray={t === 'incomplet' ? '4 2' : undefined} /></svg>{LIBELLE_TON[t]}</span>)}
      </div>
      <div className="prp-legende-groupe"><span className="prp-legende-titre">Diamètres (liquide / gaz)</span>
        {[...styles.values()].map((s) => (
          <span key={s.cle} className="prp-legende-item"><svg width="34" height="10" aria-hidden><line x1="1" y1="5" x2="33" y2="5" stroke={s.couleur} strokeWidth="4" strokeDasharray={s.motif || undefined} /></svg>
            <span><span className="prp-mono">{s.libelle}</span> <span className="discret">({s.libelleMm})</span></span></span>
        ))}
        {!styles.size && <span className="tres-petit discret">Aucun tronçon tracé.</span>}
      </div>
      <div className="prp-legende-groupe"><span className="prp-legende-titre">Symboles</span>
        {types.length > 0 && <span className="prp-legende-item">Unités : {types.join(', ').replace(/_/g, ' ')}</span>}
        <span className="prp-legende-item"><svg width="16" height="16" aria-hidden><polygon points="8,1 15,8 8,15 1,8" fill="#fff" stroke="#111827" strokeWidth="2" /></svg>Dérivation (référence du kit)</span>
        <span className="prp-legende-item"><svg width="22" height="10" aria-hidden><line x1="1" y1="5" x2="21" y2="5" stroke="#0B6FD6" strokeWidth="2" strokeDasharray="5 3" /></svg>Condensats (P = relevage)</span>
        <span className="prp-legende-item"><svg width="22" height="10" aria-hidden><line x1="1" y1="5" x2="21" y2="5" stroke="#2E7D32" strokeWidth="1.5" /></svg>Bus (ordre de chaînage)</span>
        <span className="prp-legende-item"><svg width="22" height="10" aria-hidden><line x1="1" y1="5" x2="21" y2="5" stroke="#C62828" strokeWidth="1.5" strokeDasharray="2 3" /></svg>Alimentation (indicative)</span>
        <span className="prp-legende-item"><span className="prp-pastille-leg prp-pastille-bloquant">1</span>Bloquant <span className="prp-pastille-leg prp-pastille-verifier">2</span>À vérifier</span>
      </div>
    </div>
  );
}

/** Plan d'une variante avec ses calques (réutilisé par le comparateur côte à côte). */
export function PlanVariante({ v, plans, pieces, actifs, compact, cible, points, pointChoisi, choisirPoint, selection, choisir, planId, changerPlan, etiquette, entete }: {
  v: VarianteProposition; plans: PlanVue[]; pieces: PieceVue[]; actifs: Set<Calque>; compact?: boolean; cible?: { x: number; y: number; jeton: number } | null;
  points: PointLocalise[]; pointChoisi: string | null; choisirPoint: (id: string) => void; selection: Selection; choisir: (s: Selection) => void;
  planId: string; changerPlan: (id: string) => void; etiquette: string; entete?: ReactNode;
}) {
  const plan = plans.find((p) => p.id === planId) ?? plans[0];
  const styles = useMemo(() => stylesDiametres(v.troncons), [v]);
  const zone = useMemo(() => (plan ? emprise([...pieces.filter((p) => p.planId === plan.id).map((p) => p.polygone),
    ...v.groupesExterieurs.filter((g) => g.planId === plan.id).map((g) => g.degagement ?? g.emprise ?? [[g.x, g.y] as [number, number]])]) : null), [plan, pieces, v]);
  if (!plan) return null;
  const nomPlan = (id: string) => plans.find((p) => p.id === id)?.nom.replace(/ — .*$/, '') ?? 'autre étage';
  const parPlan = (id: string) => v.unites.filter((u) => u.planId === id).length;
  return (
    <div className="pile prp-plan-variante" style={{ gap: 8 }}>
      {(plans.length > 1 || entete) && (
        <div className="prp-plan-entete">
          {plans.length > 1 && (
            <div className="segment prp-segment-plans" role="group" aria-label="Plan affiché">
              {plans.map((p) => <button key={p.id} type="button" aria-pressed={p.id === plan.id} onClick={() => changerPlan(p.id)}>{nomPlan(p.id)} <span className="compteur">{parPlan(p.id)}</span></button>)}
            </div>
          )}
          {entete}
        </div>
      )}
      <PlanCalque plan={plan} compact={compact} cible={cible} emprise={zone} libelle={`${etiquette} — plan ${plan.nom}`}
        surimpression={<span className="prp-tampon"><AlertTriangle size={14} aria-hidden /> {etiquette}</span>}
        calque={(u) => <><DefsCalques /><CalquesVariante v={v} planId={plan.id} u={u} pieces={pieces} actifs={actifs} styles={styles} points={points}
          pointChoisi={pointChoisi} choisirPoint={choisirPoint} selection={selection} choisir={choisir} nomPlan={nomPlan} /></>} />
    </div>
  );
}

export const etiquetteVariante = (l: Pick<LigneVue, 'origine'>) => (l.origine === 'technicien' ? 'Proposition du technicien — évaluée par TRAME' : 'Proposé par TRAME — à valider');

export function Visuel({ dossierId, calcul, plans, pieces, varianteId, choisirVariante, rafraichir, actifs, setActifs }: {
  dossierId: string; calcul: CalculVue; plans: PlanVue[]; pieces: PieceVue[]; varianteId: string; choisirVariante: (id: string) => void; rafraichir: () => Promise<unknown>;
  /** Calques affichés : tenus par l'onglet, pour que le PDF du plan reprenne ceux de l'écran. */
  actifs: Set<Calque>; setActifs: (f: (s: Set<Calque>) => Set<Calque>) => void;
}) {
  const { peut } = useSession();
  const couts = peut('devis.couts');
  const [params, setParams] = useSearchParams();
  const [pointChoisi, setPointChoisi] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [cible, setCible] = useState<{ x: number; y: number; jeton: number } | null>(null);
  const [voirCalques, setVoirCalques] = useState(false);
  const ligne = calcul.variantes.find((x) => x.id === varianteId) ?? calcul.variantes[0];
  const detail = useDetail(dossierId, ligne?.id ?? null);
  const v = detail.data?.variante;
  const piecesMap = useMemo(() => new Map(pieces.map((p) => [p.id, { planId: p.planId, polygone: p.polygone }])), [pieces]);
  const points = useMemo(() => (v ? pointsLocalises(v, piecesMap) : []), [v, piecesMap]);
  const planId = plans.some((p) => p.id === params.get('plan')) ? params.get('plan')! : plans[0]?.id;
  const changerPlan = (id: string) => { const n = new URLSearchParams(params); n.set('plan', id); setParams(n, { replace: true }); };
  const allerA = (ou: { planId: string; x: number; y: number } | null) => {
    if (!ou) return;
    if (ou.planId !== planId) changerPlan(ou.planId);
    setCible({ x: ou.x, y: ou.y, jeton: Date.now() });
  };
  const choisirPoint = (id: string) => {
    setPointChoisi(id);
    const p = points.find((x) => x.id === id);
    if (p) { allerA(p.ou); document.getElementById(`prp-point-${id}`)?.scrollIntoView({ block: 'nearest' }); }
  };
  const basculer = (c: Calque) => setActifs((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  if (!ligne) return null;
  return (
    <div className="pile">
      <div className="prp-cartes" role="group" aria-label="Variantes classées">
        {calcul.variantes.map((l) => <CarteVariante key={l.id} l={l} choisie={l.id === ligne.id} couts={couts} choisir={() => { choisirVariante(l.id); setSelection(null); setPointChoisi(null); }} />)}
      </div>
      <p className="petit prp-explication"><strong>Rang {ligne.rang} — {ligne.libelle}.</strong> {ligne.explication}</p>
      {calcul.doublons.length > 0 && <p className="tres-petit discret">Variante(s) identique(s) non répétée(s) : {calcul.doublons.map((d) => `${LIBELLES_STRATEGIE[d.strategie as keyof typeof LIBELLES_STRATEGIE] ?? d.strategie} = ${d.identiqueA}`).join(' ; ')}.</p>}

      {detail.isLoading ? <Chargement lignes={4} libelle="Chargement de la variante…" /> : detail.isError || !v ? <EtatErreur erreur={detail.error} reessayer={() => detail.refetch()} /> : (
        <>
          <div className="prp-disposition">
            <PlanVariante v={v} plans={plans} pieces={pieces} actifs={actifs} cible={cible} points={points} pointChoisi={pointChoisi} choisirPoint={choisirPoint}
              selection={selection} choisir={setSelection} planId={planId} changerPlan={changerPlan} etiquette={etiquetteVariante(ligne)}
              entete={<>
                <div className="prp-calques-barre">
                  <button type="button" className="btn btn-compact prp-calques-bouton" aria-expanded={voirCalques} onClick={() => setVoirCalques((x) => !x)}>
                    <Layers aria-hidden /> Calques ({actifs.size}/{CALQUES.length})
                  </button>
                  <div className={`prp-calques ${voirCalques ? 'est-ouvert' : ''}`} role="group" aria-label="Calques affichés">
                    {CALQUES.map(([c, lib]) => <button key={c} type="button" className="cch-puce prp-puce" aria-pressed={actifs.has(c)} onClick={() => basculer(c)}>{lib}</button>)}
                  </div>
                </div>
              </>} />
            <aside className="pile prp-cote" aria-label="Points à traiter et objet choisi">
              {selection && <FicheSelection v={v} s={selection} pieces={pieces} />}
              <ListePoints points={points} choisi={pointChoisi} choisir={choisirPoint} />
              <Decision dossierId={dossierId} ligne={ligne} detail={detail.data!} entreesValidees={calcul.entreesValidees} rafraichir={rafraichir} />
            </aside>
          </div>
          <Legende v={v} styles={stylesDiametres(v.troncons)} />
          <Systemes v={v} />
          {ligne.origine === 'trame' && <div className="prp-materiel-bloc"><MaterielVariante dossierId={dossierId} propositionId={ligne.id} /></div>}
        </>
      )}
    </div>
  );
}

function ListePoints({ points, choisi, choisir }: { points: PointLocalise[]; choisi: string | null; choisir: (id: string) => void }) {
  const bloquants = points.filter((p) => p.niveau === 'bloquant').length;
  return (
    <Panneau titre={`Points à traiter (${points.length})`} actions={bloquants ? <Badge ton="danger">{bloquants} bloquant{bloquants > 1 ? 's' : ''}</Badge> : <Badge ton="succes">Aucun bloquant</Badge>}>
      {!points.length ? <p className="petit">Aucun point bloquant ni « à vérifier ».</p> : (
        <ol className="prp-points">
          {points.map((p) => (
            <li key={p.id} id={`prp-point-${p.id}`}>
              <button type="button" className={`prp-point ${choisi === p.id ? 'est-choisi' : ''}`} aria-pressed={choisi === p.id} onClick={() => choisir(p.id)}>
                <span className={`prp-pastille-leg ${p.niveau === 'bloquant' ? 'prp-pastille-bloquant' : 'prp-pastille-verifier'}`}>{p.numero}</span>
                <span className="prp-point-texte">
                  <span className="petit">{p.message}</span>
                  <span className="tres-petit secondaire">
                    <span className="prp-mono">{p.code}</span> · {p.niveau === 'bloquant' ? 'bloquant' : 'à vérifier'} · règle {p.regle}
                    {p.valeur != null && ` · valeur ${nombre(p.valeur, 2)}`}{p.limite != null && ` · limite ${nombre(p.limite, 2)}`}
                    {p.source && ` · source : ${p.source}`}{!p.ou && ' · point général (pas d’emplacement sur le plan)'}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </Panneau>
  );
}

function FicheSelection({ v, s, pieces }: { v: VarianteProposition; s: NonNullable<Selection>; pieces: PieceVue[] }) {
  if (s.type === 'piece') {
    const p = pieces.find((x) => x.id === s.id); const r = resumePiece(v, s.id);
    return (
      <Panneau titre={p?.nom ?? 'Pièce'}>
        <dl className="fiche">
          <div><dt>État</dt><dd>{LIBELLE_TON[r.etat.ton]}</dd></div>
          <div><dt>Besoin froid</dt><dd>{kw(r.besoin?.froidW)}</dd></div>
          <div><dt>Installé froid</dt><dd>{r.affectation ? kw(r.affectation.installeFroidW) : 'aucune unité'}</dd></div>
          <div><dt>Couverture (froid / sensible)</dt><dd>{pct(r.affectation?.couverture.froid)} / {pct(r.affectation?.couverture.sensible)}</dd></div>
          <div><dt>Besoin chaud</dt><dd>{kw(r.besoin?.chaudW)}{r.chaud ? ` (couvert à ${r.chaud})` : ''}</dd></div>
          <div><dt>Unités</dt><dd>{r.affectation?.nombre ? `${r.affectation.nombre} × ${r.affectation.reference}` : '—'}</dd></div>
        </dl>
        {r.affectation?.explication.map((e, i) => <p key={i} className="tres-petit secondaire" style={{ marginTop: 6 }}>{e}</p>)}
        {r.besoin?.incomplet.length ? <p className="tres-petit cch-texte-ambre">Besoin incomplet : {r.besoin.incomplet.join(', ')}.</p> : null}
      </Panneau>
    );
  }
  if (s.type === 'unite') {
    const u = v.unites.find((x) => x.id === s.id); if (!u) return null;
    return (
      <Panneau titre={`Unité ${u.repere}`}>
        <dl className="fiche">
          <div><dt>Modèle</dt><dd className="prp-mono">{u.reference}</dd></div><div><dt>Type</dt><dd>{u.type.replace(/_/g, ' ')}</dd></div>
          <div><dt>Froid / chaud</dt><dd>{kw(u.froidW)} / {kw(u.chaudW)}</dd></div><div><dt>Sensible</dt><dd>{kw(u.sensibleW)}</dd></div>
          <div><dt>Indice</dt><dd>{u.indice ?? 'à relever'}</dd></div><div><dt>Altitude de pose</dt><dd>{u.altitudeM != null ? `${nombre(u.altitudeM, 2)} m` : 'à saisir'}</dd></div>
        </dl>
      </Panneau>
    );
  }
  if (s.type === 'troncon') {
    const t = v.troncons.find((x) => x.id === s.id); if (!t) return null;
    const aval = t.uisAval.map((id) => v.unites.find((u) => u.id === id)?.repere ?? id);
    return (
      <Panneau titre="Tronçon frigorifique">
        <dl className="fiche">
          <div><dt>Liquide / gaz</dt><dd className="prp-mono">{t.liquide ? `${t.liquide.pouces}″ (${t.liquide.mm.replace('.', ',')} mm)` : 'à relever'} / {t.gaz ? `${t.gaz.pouces}″ (${t.gaz.mm.replace('.', ',')} mm)` : 'à relever'}</dd></div>
          <div><dt>Σ indices en aval</dt><dd>{t.sommeIndicesAval ?? 'inconnue'}</dd></div>
          <div><dt>Longueur réelle / équivalente</dt><dd>{metres(t.longueurReelleM, 2)} / {metres(t.longueurEquivalenteM, 2)}</dd></div>
          <div><dt>Verticale</dt><dd>{t.verticaleM != null ? metres(t.verticaleM, 2) : '—'}</dd></div>
          <div><dt>Catégorie</dt><dd>{t.categorie === 'principal' ? 'Principal' : t.categorie === 'entre_derivations' ? 'Entre dérivations' : 'Vers unité'}</dd></div>
          <div><dt>Traversées de murs</dt><dd>{t.traverseesMurs}{t.traverseesCoupeFeu ? ` (dont ${t.traverseesCoupeFeu} coupe-feu)` : ''}</dd></div>
          <div><dt>Unités en aval</dt><dd>{aval.join(', ') || '—'}</dd></div>
          <div><dt>Notice</dt><dd>{t.page ? `page ${t.page}` : 'règle à relever'}{t.majore ? ' · majoré' : ''}{t.plafonne ? ' · plafonné' : ''}</dd></div>
        </dl>
      </Panneau>
    );
  }
  if (s.type === 'ue') {
    const g = v.groupesExterieurs.find((x) => x.id === s.id); const sy = v.systemes.find((x) => x.id === g?.systemeId); if (!g) return null;
    return (
      <Panneau titre={`Groupe extérieur ${g.repere}`}>
        <dl className="fiche">
          <div><dt>Modèle</dt><dd className="prp-mono">{g.reference}</dd></div><div><dt>Taux de raccordement</dt><dd>{sy?.tauxRaccordementPct != null ? `${Math.round(sy.tauxRaccordementPct)} %` : 'à calculer'}</dd></div>
          <div><dt>Σ indices</dt><dd>{sy?.sommeIndices ?? '—'}</dd></div><div><dt>Imposé</dt><dd>{g.imposee ? 'oui' : 'non'}</dd></div>
        </dl>
        {sy && <p className="tres-petit secondaire" style={{ marginTop: 6 }}>{sy.motif}</p>}
      </Panneau>
    );
  }
  if (s.type === 'noeud') {
    const n = v.noeuds.find((x) => x.id === s.id); if (!n) return null;
    return <Panneau titre={n.type === 'collecteur' ? 'Collecteur' : 'Dérivation'}><p className="petit"><span className="prp-mono">{n.kit?.reference ?? 'référence à choisir'}</span>{n.kit?.sommeIndicesAval != null ? ` · Σ indices en aval ${n.kit.sommeIndicesAval}` : ''}</p></Panneau>;
  }
  const c = v.condensats.find((x) => x.id === s.id); if (!c) return null;
  return (
    <Panneau titre="Condensats">
      <dl className="fiche">
        <div><dt>Longueur</dt><dd>{metres(c.longueurM, 2)}</dd></div>
        <div><dt>Dénivelé nécessaire / disponible</dt><dd>{metres(c.deniveleNecessaireM, 2)} / {metres(c.deniveleDisponibleM, 2)}</dd></div>
        <div><dt>Pompe</dt><dd>{c.pompe === 'non' ? 'non' : c.pompe === 'integree' ? 'intégrée' : c.pompe === 'relevage_a_prevoir' ? 'relevage à prévoir' : 'à vérifier'}</dd></div>
      </dl>
    </Panneau>
  );
}

function Systemes({ v }: { v: VarianteProposition }) {
  if (!v.systemes.length) return null;
  return (
    <section className="pile prp-systemes" style={{ gap: 8 }} aria-labelledby="prp-sys-titre">
      <h3 id="prp-sys-titre" className="cch-titre-tableau">Systèmes <span className="compteur">{v.systemes.length}</span></h3>
      <div className="tableau-conteneur">
        <table className="tableau prp-tableau-sys">
          <thead><tr><th>Système</th><th>Groupe extérieur</th><th className="num">Σ indices</th><th className="num">Taux</th><th className="num">Plus longue liaison</th><th className="num">Charge estimée</th><th>Motif</th></tr></thead>
          <tbody>
            {v.systemes.map((s) => (
              <tr key={s.id}>
                <td>{s.repere}{s.logement ? ' (logement)' : ''}</td><td className="prp-mono">{s.referenceUe ?? 'à choisir'}</td><td className="num">{s.sommeIndices}</td>
                <td className="num">{s.tauxRaccordementPct != null ? `${Math.round(s.tauxRaccordementPct)} %` : '—'}</td>
                <td className="num">{metres(s.longueurs.plusLongueReelleM)} ({metres(s.longueurs.plusLongueEquivalenteM)} éq.)</td>
                <td className="num">{s.charge?.grammes ? `${nombre(Number(s.charge.grammes) / 1000, 2)} kg` : 'incomplète'}</td>
                <td className="tres-petit">{s.motif}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

